
import { useState } from "react";
import apiClient from '../services/apiClient';
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import StakeTable, { Column } from "./GameEngine/StakeTable";
import { useNavigate } from "react-router-dom";

const SubAdminMange = () => {
	const queryClient = useQueryClient();
	const navigate = useNavigate();



	const [formData, setFormData] = useState({
		username: "",
		email: "",
		password: "",
		role: "manager",
		amount: "",
	});
	const [message, setMessage] = useState("");
	const [showModal, setShowModal] = useState(false);
	const [searchTerm, setSearchTerm] = useState("");

	const [page, setPage] = useState(1);
	const [limit, setLimit] = useState(50);

	const { data: adminsRes, isLoading: isLoadingAdmins } = useQuery({
		queryKey: ['managers', page, limit, searchTerm],
		queryFn: async () => {
			const token = localStorage.getItem("token");
			if (!token) throw new Error("No token");
			const offset = (page - 1) * limit;
			let url = `/collaborator/list?role=manager&limit=${limit}&offset=${offset}`;
			if (searchTerm) {
				url += `&search=${encodeURIComponent(searchTerm)}`;
			}
			const response = await apiClient.get(url, {
				headers: { Authorization: `Bearer ${token}` },
			});
			return response.data;
		}
	});

	const admins = adminsRes?.data || [];
	const total = adminsRes?.total || 0;
	const totalPages = Math.ceil(total / limit);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
		setFormData({ ...formData, [e.target.name]: e.target.value });
	};



	const createMutation = useMutation({
		mutationFn: async (payload: any) => {
			const token = localStorage.getItem("token");
			return apiClient.post(`/collaborator/manager/create`, payload, {
				headers: { Authorization: `Bearer ${token}` }
			});
		},
		onSuccess: (response) => {
			setMessage(response.data.message);
			setFormData({ username: "", email: "", password: "", role: "manager", amount: "" });
			setShowModal(false);
			queryClient.invalidateQueries({ queryKey: ['managers'] });
		},
		onError: (error: any) => {
			setMessage(error.response?.data?.message || "Failed to create admin");
		}
	});

	const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setMessage("");
		const payload = {
			username: formData.username,
			email: formData.email,
			password: formData.password,
			role: formData.role,
			amount: formData.amount || "0",
			creditType: "initial",
		};
		createMutation.mutate(payload);
	};

	const deleteMutation = useMutation({
		mutationFn: async (uid: string | number) => {
			const token = localStorage.getItem("token");
			return apiClient.delete(`/admin/deleteAdmin/${uid}`, {
				headers: { Authorization: `Bearer ${token}` }
			});
		},
		onSuccess: (response) => {
			setMessage(response.data.message);
			queryClient.invalidateQueries({ queryKey: ['managers'] });
			setTimeout(() => setMessage(""), 3000);
		},
		onError: (error: any) => {
			setMessage(error.response?.data?.message || "Failed to delete admin");
		}
	});

	const toggleStatusMutation = useMutation({
		mutationFn: async (id: string | number) => {
			const token = localStorage.getItem("token");
			return apiClient.put(`/collaborator/toggle-status/${id}`, {}, {
				headers: { Authorization: `Bearer ${token}` }
			});
		},
		onSuccess: (response) => {
			setMessage(response.data.message);
			queryClient.invalidateQueries({ queryKey: ['managers'] });
			setTimeout(() => setMessage(""), 3000);
		},
		onError: (error: any) => {
			setMessage(error.response?.data?.message || "Failed to toggle status");
		}
	});

	const handleToggleStatus = (id: string | number, name: string, isActive: boolean) => {
		const actionText = isActive ? 'block' : 'unblock';
		if (window.confirm(`Are you sure you want to ${actionText} manager "${name}"?`)) {
			toggleStatusMutation.mutate(id);
		}
	};

	const handleDeleteAdmin = (uid: string | number, name: string) => {
		if (window.confirm(`Are you sure you want to delete admin "${name}"?`)) {
			deleteMutation.mutate(uid);
		}
	};

	const columns: Column[] = [
		{ key: 'sn', label: 'S.No.', render: (_, __, i) => (page - 1) * limit + i + 1 },
		{ key: 'role', label: 'Role', render: (val) => <span className="bg-blue-900/50 text-blue-200 px-2 py-1 rounded text-xs font-semibold uppercase">{val}</span> },
		{ key: 'name', label: 'Name' },
		{ key: 'email', label: 'Email' },
		{ key: 'referalcode', label: 'Code', render: (val) => <span className="font-bold text-green-400">{val}</span> },
		{ key: 'refferLink', label: 'Refer Link', render: (_, item: any) => <span className="font-bold text-green-400">{item.referral_link}</span> },
		{ 
			key: 'affiliate_count', 
			label: 'Affiliates', 
			render: (val, item: any) => (
				<span 
					onClick={() => navigate(`/collaboratorUser?managerFilter=${encodeURIComponent(item.username)}`)}
					className="text-indigo-400 hover:text-indigo-300 font-bold cursor-pointer underline decoration-dotted"
				>
					{val || 0}
				</span>
			)
		},
		{ 
			key: 'user_count', 
			label: 'Users', 
			render: (val, item: any) => (
				<span 
					onClick={() => navigate(`/users?subadminFilter=${encodeURIComponent(item.username)}`)}
					className="text-indigo-400 hover:text-indigo-300 font-bold cursor-pointer underline decoration-dotted"
				>
					{val || 0}
				</span>
			)
		},
		{
			key: 'status',
			label: 'Status',
			render: (_, item: any) => (
				<span className={`px-2.5 py-1 rounded text-xs font-bold whitespace-nowrap ${item.is_active ? 'bg-emerald-950 text-emerald-400 border border-emerald-500/20' : 'bg-red-950 text-red-400 border border-red-500/20'}`}>
					{item.is_active ? 'Active' : 'Blocked'}
				</span>
			)
		},
		{
			key: 'actions', label: 'Account Actions', textAlign: 'center', render: (_, item: any) => (
				<div className="flex gap-2 justify-center">
					<button
						onClick={() => handleToggleStatus(item.id, item.username, item.is_active)}
						className={`${item.is_active ? 'bg-amber-600 hover:bg-amber-700' : 'bg-emerald-600 hover:bg-emerald-700'} text-white py-1.5 px-3 rounded text-xs font-bold transition-all whitespace-nowrap`}
						disabled={toggleStatusMutation.isPending}
					>
						{item.is_active ? "Block" : "Unblock"}
					</button>
					<button
						onClick={() => handleDeleteAdmin(item.id, item.username)}
						className="bg-red-600 hover:bg-red-700 text-white py-1.5 px-3 rounded text-xs font-bold transition-all whitespace-nowrap"
						disabled={deleteMutation.isPending}
					>
						{deleteMutation.isPending ? "Deleting..." : "Delete"}
					</button>
				</div>
			)
		}
	];

	return (
		<div className="ml-0 lg:ml-[256px] min-h-screen p-4 sm:p-6 text-white bg-[#151929]">
			<h2 className="text-xl sm:text-3xl font-bold mb-6 sm:mb-8 text-white">Managers Management</h2>

			{message && (
				<div className={`mb-4 text-center py-2 px-4 rounded-lg shadow-md ${message.toLowerCase().includes('success') ? 'bg-green-600' : 'bg-red-600'}`}>
					{message}
				</div>
			)}



			<div className="mb-6">
				<StakeTable
					isLoading={isLoadingAdmins}
					title=""
					columns={columns}
					data={admins}
					searchPlaceholder="Search by username or email..."
					onSearch={(val) => {
						setSearchTerm(val);
						setPage(1);
					}}
					emptyMessage={isLoadingAdmins ? "Loading managers..." : "No managers found."}
					hasSidebarMargin={false}
					page={page}
					totalPages={totalPages}
					total={total}
					limit={limit}
					onLimitChange={(newLimit) => {
						setLimit(newLimit);
						setPage(1);
					}}
					onPageChange={(newPage) => setPage(newPage)}
					actions={
						<button
							onClick={() => setShowModal(true)}
							className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-3 rounded-lg flex items-center gap-1.5 transition-colors shadow-sm whitespace-nowrap text-sm"
						>
							<svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
								<path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
							</svg>
							Create Manager
						</button>
					}
				/>
			</div>

			{showModal && (
				<div className="fixed inset-0 bg-black bg-opacity-80 flex justify-center items-center z-50 p-4">
					<div className="bg-slate-900 border-slate-700/60 shadow-2xl border p-8 rounded-2xl shadow-2xl w-full max-w-md">
						<h2 className="text-2xl font-bold mb-6 text-center">Create New Admin</h2>
						<form onSubmit={handleSubmit} className="space-y-4">

							<div>
								<label className="block text-sm font-semibold text-gray-400 mb-1">Role</label>
								<select name="role" value={formData.role} onChange={handleChange} className="w-full p-3 rounded-lg bg-slate-950/50 border-slate-700/50 focus:border-indigo-500 text-white border focus:ring-2 focus:ring-indigo-500 outline-none cursor-pointer">
									<option value="manager">Manager</option>
									{/* <option value="manager">Agent</option>
									<option value="manager">Manager</option> */}
								</select>
							</div>

							<div>
								<label className="block text-sm font-semibold text-gray-400 mb-1">Username</label>
								<input type="text" name="username" value={formData.username} onChange={handleChange} required className="w-full p-3 rounded-lg bg-slate-950/50 border-slate-700/50 focus:border-indigo-500 text-white border focus:ring-2 focus:ring-indigo-500 outline-none" />
							</div>
							<div>
								<label className="block text-sm font-semibold text-gray-400 mb-1">Email Address</label>
								<input type="email" name="email" value={formData.email} onChange={handleChange} required className="w-full p-3 rounded-lg bg-slate-950/50 border-slate-700/50 focus:border-indigo-500 text-white border focus:ring-2 focus:ring-indigo-500 outline-none" />
							</div>
							<div>
								<label className="block text-sm font-semibold text-gray-400 mb-1">Password</label>
								<input type="password" name="password" value={formData.password} onChange={handleChange} required className="w-full p-3 rounded-lg bg-slate-950/50 border-slate-700/50 focus:border-indigo-500 text-white border focus:ring-2 focus:ring-indigo-500 outline-none" />
							</div>

							{/* <div>
								<label className="block text-sm font-semibold text-gray-400 mb-1">Initial Credits</label>
								<input type="number" name="amount" value={formData.amount} onChange={handleChange} className="w-full p-3 rounded-lg bg-slate-950/50 border-slate-700/50 focus:border-indigo-500 text-white border focus:ring-2 focus:ring-indigo-500 outline-none" />
							</div> */}
							<div className="flex gap-4 pt-4">
								<button type="button" onClick={() => setShowModal(false)} className="flex-1 p-3 bg-slate-800 hover:bg-slate-700  rounded-lg text-white font-bold transition-colors">
									Cancel
								</button>
								<button type="submit" className="flex-1 p-3 bg-indigo-600 hover:bg-indigo-700 rounded-lg text-white font-bold transition-colors shadow-lg" disabled={createMutation.isPending}>
									{createMutation.isPending ? "Processing..." : "Create"}
								</button>
							</div>
						</form>
					</div>
				</div>
			)}
		</div>
	);
};

export default SubAdminMange;
